import { useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { MoreHorizontalIcon, ReceiptIcon } from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { CreateInvoiceDialog } from '#admin/features/exhibitor/application-sections.tsx'
import { ServiceOrderSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import {
  useCancelServiceOrder,
  useInvoiceServiceOrder,
  useSubmitServiceOrder,
} from '#admin/features/exhibitor/mutations.ts'
import { serviceOrderQuery } from '#admin/features/exhibitor/queries.ts'
import {
  InvoiceSection,
  LinesSection,
  OrderSection,
  TotalsSection,
} from '#admin/features/exhibitor/service-order-sections.tsx'
import {
  NO_SERVICE_ORDER_ACTIONS,
  PLACEMENT_LABEL,
  serviceOrderActions,
} from '#admin/features/exhibitor/service-orders-api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/exhibition/service-orders/$serviceOrderId` — one service order: what it costs, its invoice,
 * and the payments against the invoice.
 *
 * The page offers a verb only when the order accepts it. The server refuses each verb again.
 */
export const Route = createFileRoute('/_authenticated/exhibition/service-orders/$serviceOrderId')({
  pendingComponent: ServiceOrderSkeleton,
  staticData: { crumb: useServiceOrderCrumb },
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(serviceOrderQuery(params.serviceOrderId))
    } catch (error) {
      // An order that does not exist is "not here". A module missing from the plan is also a 404,
      // and the ErrorView explains that one.
      if (error instanceof ApiClientError && error.status === 404 && !error.isMissingFeature) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: NotFoundView,
  component: ServiceOrderPage,
})

function useServiceOrderCrumb(): string {
  const { serviceOrderId } = Route.useParams()
  const { data } = useQuery(serviceOrderQuery(serviceOrderId))
  return data?.companyName ?? 'Service order'
}

type Dialog = 'submit' | 'invoice' | 'cancel' | null

function ServiceOrderPage() {
  const { serviceOrderId: id } = Route.useParams()
  const { data: order } = useSuspenseQuery(serviceOrderQuery(id))
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useQuery(manifestQuery())
  const [dialog, setDialog] = useState<Dialog>(null)
  const close = () => setDialog(null)
  const onOpenChange = (open: boolean) => (open ? undefined : close())

  const submit = useSubmitServiceOrder(id)
  const invoice = useInvoiceServiceOrder(id)
  const cancel = useCancelServiceOrder(id)

  const canManage = can(session, 'exhibitor.manage')
  const actions = canManage ? serviceOrderActions(order) : NO_SERVICE_ORDER_ACTIONS
  const reference = `the service order of ${order.companyName}`

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={order.companyName}
        description={`${PLACEMENT_LABEL[order.placement]} service order · ${order.applicationCode ?? 'Draft application'}`}
        actions={
          <>
            {actions.submit ? (
              <Button onClick={() => setDialog('submit')}>Submit order</Button>
            ) : null}
            {actions.invoice ? (
              <Button onClick={() => setDialog('invoice')}>Create invoice</Button>
            ) : null}
            {order.invoiceId === null ? null : (
              <Button asChild variant="outline">
                <Link to="/billing/invoices/$invoiceId" params={{ invoiceId: order.invoiceId }}>
                  <ReceiptIcon />
                  View invoice
                </Link>
              </Button>
            )}
            {actions.cancel ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <MoreHorizontalIcon />
                    More actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem variant="destructive" onSelect={() => setDialog('cancel')}>
                    Cancel order
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="this service order" />}

      <div className="grid gap-6 md:grid-cols-2">
        <OrderSection order={order} />
        <TotalsSection order={order} />
      </div>

      <LinesSection order={order} />

      {order.invoiceId === null ? null : <InvoiceSection invoiceId={order.invoiceId} />}

      <ConfirmDialog
        open={dialog === 'submit'}
        onOpenChange={onOpenChange}
        title={`Submit ${reference}?`}
        description="The server prices the order again now, from the on-site date and the installation fees. After that, the exhibitor cannot change it."
        confirmLabel="Submit order"
        busy={submit.isPending}
        onConfirm={() => submit.mutate(undefined, { onSuccess: close })}
      />

      <CreateInvoiceDialog
        open={dialog === 'invoice'}
        onOpenChange={onOpenChange}
        reference={reference}
        description="Billing issues an invoice for the services at the prices shown. After that, you cannot change or cancel the order."
        zone={manifest?.tenant.timezone ?? 'UTC'}
        busy={invoice.isPending}
        onConfirm={(dueAt) => invoice.mutate(dueAt, { onSuccess: close })}
      />

      <ConfirmDialog
        open={dialog === 'cancel'}
        onOpenChange={onOpenChange}
        title={`Cancel ${reference}?`}
        description="You cannot undo this. The exhibitor can place a new order."
        confirmLabel="Cancel order"
        destructive
        busy={cancel.isPending}
        onConfirm={() => cancel.mutate(undefined, { onSuccess: close })}
      />
    </div>
  )
}
